import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Send, CheckCircle } from 'lucide-react';

export const BookDemoModal = ({ open, onOpenChange }) => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    interest: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      onOpenChange(false);
      setFormData({ name: '', phone: '', email: '', interest: '', message: '' });
    }, 3000);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-white border-slate-200 p-0 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-[#0A1628] px-8 pt-8 pb-6">
          <DialogHeader>
            <DialogTitle
              className="text-2xl font-bold text-white"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Book Your{' '}
              <span className="text-[#E8C87A]">Free Demo</span>
            </DialogTitle>
            <p className="text-white/50 text-sm mt-2">
              Fill in your details and we'll get back to you within 24 hours.
            </p>
          </DialogHeader>
        </div>

        {submitted ? (
          <div className="px-8 py-12 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="text-xl font-bold text-[#0A1628] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Thank You!
            </h3>
            <p className="text-slate-500">We'll contact you shortly to schedule your free demo session.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#0A1628] text-sm font-medium">Full Name *</Label>
              <Input
                id="name"
                required
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="mt-1.5 border-slate-200 focus:border-[#D4A853] focus:ring-[#D4A853]/20"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-[#0A1628] text-sm font-medium">Phone *</Label>
                <Input
                  id="phone"
                  required
                  type="tel"
                  placeholder="+91 XXXXX XXXXX"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="mt-1.5 border-slate-200 focus:border-[#D4A853] focus:ring-[#D4A853]/20"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#0A1628] text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="mt-1.5 border-slate-200 focus:border-[#D4A853] focus:ring-[#D4A853]/20"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#0A1628] text-sm font-medium">I'm Interested In *</Label>
              <Select required value={formData.interest} onValueChange={(v) => handleChange('interest', v)}>
                <SelectTrigger className="mt-1.5 border-slate-200 focus:border-[#D4A853] focus:ring-[#D4A853]/20">
                  <SelectValue placeholder="Select your interest" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ielts">IELTS Coaching</SelectItem>
                  <SelectItem value="study-abroad">Study Abroad Consulting</SelectItem>
                  <SelectItem value="visa">Visa Assistance</SelectItem>
                  <SelectItem value="sop">SOP/LOR Writing</SelectItem>
                  <SelectItem value="visit-visa">Visit/Family Visa</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="message" className="text-[#0A1628] text-sm font-medium">Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Tell us about your goals..."
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className="mt-1.5 border-slate-200 focus:border-[#D4A853] focus:ring-[#D4A853]/20 min-h-[80px]"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#D4A853] hover:bg-[#C9A84C] text-[#0A1628] font-bold py-6 rounded-full text-base shadow-lg shadow-[#D4A853]/20"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit & Book Demo
            </Button>

            <p className="text-center text-xs text-slate-400">
              No fees. No pressure. Just honest guidance.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
